import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { IPanelPagoRepository } from '@/domain/repositories/IPanelPagoRepository';
import { useToast } from '@/presentation/contexts/ToastContext';
import { usePlans } from '@/presentation/hooks/usePlans';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { Button } from '@/presentation/components/common';
import { ModalShell } from '@/presentation/components/common/ModalShell';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import type { PanelPagoFormData } from '@/domain/repositories/IPanelPagoRepository';

const isTenantGuid = (id: string | number): id is string =>
  typeof id === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);

interface RegisterPaymentModalProps {
  isOpen: boolean;
  tenant: TenantGridDTO;
  onClose: () => void;
  onSuccess: () => void;
}

// Mock de tipos de pago - en producción vendría de API
const mockPaymentTypes = [
  { id: 1, descripcion: 'Transferencia Bancaria' },
  { id: 2, descripcion: 'Efectivo' },
  { id: 3, descripcion: 'Tarjeta de Crédito' },
  { id: 4, descripcion: 'Cheque' },
  { id: 5, descripcion: 'Mercado Pago' },
];

export const RegisterPaymentModal: React.FC<RegisterPaymentModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const { plans } = usePlans();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPeriod, setIsLoadingPeriod] = useState(true);
  const [hasPeriodInDB, setHasPeriodInDB] = useState(false);
  const [vencerDeuda, setVencerDeuda] = useState(false);
  const [deudaAcumulada, setDeudaAcumulada] = useState<number>(0);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [formData, setFormData] = useState<PanelPagoFormData>(() => {
    const initialMonto = tenant.planAdminId && plans.length > 0
      ? (plans.find(p => p.id === tenant.planAdminId)?.precio || 0)
      : 0;
    
    return {
      ...(isTenantGuid(tenant.id) ? { tenantId: tenant.id } : { clinicaId: Number(tenant.id) }),
      fechaPago: new Date(),
      fechaPeriodoPago: '',
      monto: initialMonto,
      planId: tenant.planAdminId?.toString() || null,
      medioPago: null,
      notas: '',
      nombreArchivoComprobante: undefined,
    };
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const hasInitialized = React.useRef(false);

  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;
      loadPeriodoPago();
      loadDeudaAcumulada();
      
      // Detectar si es modo edición (tenant ya tiene pago registrado)
      if (tenant.estadoPagoId === 1) {
        setIsEditMode(true);
        loadExistingPayment();
      } else {
        setIsEditMode(false);
        // Resetear formData con valores del tenant cuando se abre el modal en modo creación
        if (tenant.planAdminId && plans.length > 0) {
          const plan = plans.find(p => p.id === tenant.planAdminId);
          if (plan) {
            setFormData(prev => ({
              ...prev,
              monto: plan.precio,
              planId: plan.id.toString()
            }));
          }
        }
      }
    } else if (!isOpen) {
      hasInitialized.current = false;
      setVencerDeuda(false);
      setDeudaAcumulada(0);
      setIsEditMode(false);
      // Resetear formData cuando se cierra el modal
      if (tenant.planAdminId && plans.length > 0) {
        const plan = plans.find(p => p.id === tenant.planAdminId);
        if (plan) {
          setFormData({
            ...(isTenantGuid(tenant.id) ? { tenantId: tenant.id } : { clinicaId: Number(tenant.id) }),
            fechaPago: new Date(),
            fechaPeriodoPago: '',
            monto: plan.precio,
            planId: plan.id.toString(),
            medioPago: null,
            notas: '',
            nombreArchivoComprobante: undefined,
          });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tenant.id, tenant.estadoPagoId]);

  // Actualizar monto y plan cuando se abre el modal o cuando cambian los planes
  useEffect(() => {
    if (isOpen && tenant.planAdminId && plans.length > 0 && !vencerDeuda) {
      const plan = plans.find(p => p.id === tenant.planAdminId);
      if (plan) {
        setFormData(prev => ({
          ...prev,
          monto: plan.precio,
          planId: plan.id.toString()
        }));
      }
    }
  }, [isOpen, tenant.planAdminId, plans, vencerDeuda]);

  const handlePlanChange = (planId: string) => {
    const plan = plans.find(p => p.id === parseInt(planId));
    if (plan && !vencerDeuda) {
      setFormData(prev => ({ ...prev, monto: plan.precio, planId }));
    } else {
      setFormData(prev => ({ ...prev, planId }));
    }
  };

  const loadPeriodoPago = async () => {
    setIsLoadingPeriod(true);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const periodo = await repository.getPeriodoPagoByTenantId(tenant.id);
      
      if (periodo) {
        setFormData(prev => ({ ...prev, fechaPeriodoPago: periodo.fechaPeriodoPago }));
        setHasPeriodInDB(true);
      } else {
        // Generar períodos disponibles
        setHasPeriodInDB(false);
      }
    } catch (err) {
      console.error('Error loading periodo:', err);
      setHasPeriodInDB(false);
    } finally {
      setIsLoadingPeriod(false);
    }
  };

  const loadDeudaAcumulada = async () => {
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const tenantPayments = await repository.getByTenantId(tenant.id);
      
      if (tenantPayments.length > 0) {
        // Ordenar por fecha de período (más reciente primero) y obtener la deuda acumulada
        tenantPayments.sort((a, b) => {
          const dateA = new Date(a.fechaPeriodoPago).getTime();
          const dateB = new Date(b.fechaPeriodoPago).getTime();
          return dateB - dateA;
        });
        setDeudaAcumulada(tenantPayments[0].deudaAcumulada || 0);
      } else {
        setDeudaAcumulada(0);
      }
    } catch (err) {
      console.error('Error loading deuda acumulada:', err);
      setDeudaAcumulada(0);
    }
  };

  const loadExistingPayment = async () => {
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const tenantPayments = await repository.getByTenantId(tenant.id);
      
      if (tenantPayments.length > 0) {
        // Ordenar por fecha de período (más reciente primero) y obtener el último pago
        tenantPayments.sort((a, b) => {
          const dateA = new Date(a.fechaPeriodoPago).getTime();
          const dateB = new Date(b.fechaPeriodoPago).getTime();
          return dateB - dateA;
        });
        
        const lastPayment = tenantPayments[0];
        
        // Precargar el formulario con los datos del pago existente
        setFormData({
          ...(isTenantGuid(tenant.id) ? { tenantId: tenant.id } : { clinicaId: Number(tenant.id) }),
          fechaPago: lastPayment.fechaPago ? new Date(lastPayment.fechaPago) : new Date(),
          fechaPeriodoPago: lastPayment.fechaPeriodoPago || '',
          monto: lastPayment.monto || 0,
          planId: lastPayment.planId || tenant.planAdminId?.toString() || null,
          medioPago: lastPayment.medioPago || null,
          notas: lastPayment.notas || '',
          nombreArchivoComprobante: lastPayment.nombreArchivoComprobante,
        });
      }
    } catch (err) {
      console.error('Error loading existing payment:', err);
      showError(t('pages.tenants.errorLoadingPayment'));
    }
  };

  const generatePeriodOptions = () => {
    const today = new Date();
    const options = [];
    
    // Mes anterior
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    options.push({
      value: `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`,
      label: `${lastMonth.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}`,
    });
    
    // Mes actual
    options.push({
      value: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      label: `${today.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })} (Actual)`,
    });
    
    // Mes siguiente
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    options.push({
      value: `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`,
      label: `${nextMonth.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}`,
    });
    
    return options;
  };

  const handleVencerDeudaChange = async (checked: boolean) => {
    setVencerDeuda(checked);
    if (checked) {
      // Obtener deuda acumulada directamente
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const tenantPayments = await repository.getByTenantId(tenant.id);
      let currentDeuda = 0;
      
      if (tenantPayments.length > 0) {
        // Ordenar por fecha de período (más reciente primero) y obtener la deuda acumulada
        tenantPayments.sort((a, b) => {
          const dateA = new Date(a.fechaPeriodoPago).getTime();
          const dateB = new Date(b.fechaPeriodoPago).getTime();
          return dateB - dateA;
        });
        currentDeuda = tenantPayments[0].deudaAcumulada || 0;
        setDeudaAcumulada(currentDeuda);
      }
      
      // Obtener monto del plan actual
      const planId = formData.planId ? parseInt(formData.planId) : tenant.planAdminId;
      const plan = planId ? plans.find(p => p.id === planId) : null;
      const montoPlan = plan?.precio || 0;
      
      // Monto total = monto del plan del periodo actual + deuda acumulada
      const montoTotal = montoPlan + currentDeuda;
      setFormData(prev => ({ ...prev, monto: montoTotal }));
    } else {
      // Restaurar monto según plan (solo el monto del periodo actual)
      const planId = formData.planId ? parseInt(formData.planId) : null;
      if (planId) {
        const plan = plans.find(p => p.id === planId);
        if (plan) {
          setFormData(prev => ({ ...prev, monto: plan.precio }));
        }
      } else if (tenant.planAdminId) {
        const plan = plans.find(p => p.id === tenant.planAdminId);
        if (plan) {
          setFormData(prev => ({ ...prev, monto: plan.precio }));
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.fechaPago) {
      showError(t('pages.tenants.validation.paymentDateRequired'));
      return;
    }
    if (formData.fechaPago > new Date()) {
      showError(t('pages.tenants.validation.paymentDateNotFuture'));
      return;
    }
    if (!formData.fechaPeriodoPago) {
      showError(t('pages.tenants.validation.periodRequired'));
      return;
    }
    if (!formData.monto || formData.monto < 0.01) {
      showError(t('pages.tenants.validation.amountRequired'));
      return;
    }
    if (!formData.medioPago) {
      showError(t('pages.tenants.validation.paymentMethodRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      
      // Preparar datos del pago (abonado limpia deuda; “vencer deuda” sella períodos previos)
      const paymentData: PanelPagoFormData = {
        ...formData,
        estadoPagoId: 1, // Abonado
        deudaAcumulada: 0,
        saldarDeuda: vencerDeuda || deudaAcumulada > 0,
      };

      // Si hay archivo, primero subirlo
      if (selectedFile) {
        // TODO: Implementar subida de archivo
        paymentData.nombreArchivoComprobante = selectedFile.name;
      }

      await repository.updateOrCreate(paymentData);
      
      success(isEditMode ? t('pages.tenants.paymentUpdatedSuccess') : t('pages.tenants.paymentRegisteredSuccess'));
      onSuccess();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al registrar pago');
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const periodOptions = generatePeriodOptions();

  const fieldClass =
    'block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:cursor-not-allowed disabled:bg-slate-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-600';

  const labelClass = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={isEditMode ? t('pages.tenants.editPayment') : t('pages.tenants.registerPayment')}
      subtitle={tenant.nombre}
      closeOnOverlay={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="register-payment-form"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoadingPeriod || isLoading}
            className="w-full sm:w-auto"
          >
            {isEditMode ? t('common.save') : t('pages.tenants.registerPayment')}
          </Button>
        </>
      }
    >
      <form id="register-payment-form" onSubmit={handleSubmit} className="space-y-3">
        {isLoadingPeriod ? (
          <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('common.loading')}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="reg-pay-date">
                  {t('pages.tenants.paymentDate')}
                </label>
                <input
                  id="reg-pay-date"
                  type="date"
                  value={
                    formData.fechaPago
                      ? new Date(formData.fechaPago).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fechaPago: e.target.value ? new Date(e.target.value) : null,
                    })
                  }
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className={fieldClass}
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="reg-pay-period">
                  {t('pages.tenants.paymentPeriod')}
                </label>
                <select
                  id="reg-pay-period"
                  value={formData.fechaPeriodoPago}
                  onChange={(e) =>
                    setFormData({ ...formData, fechaPeriodoPago: e.target.value })
                  }
                  disabled={hasPeriodInDB}
                  className={fieldClass}
                  required
                >
                  <option value="">{t('pages.tenants.selectPeriod')}</option>
                  {periodOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {hasPeriodInDB && (
                  <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                    {t('pages.tenants.periodFromDB')}
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass} htmlFor="reg-pay-plan">
                  {t('pages.tenants.plan')}
                </label>
                <select
                  id="reg-pay-plan"
                  value={formData.planId || ''}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className={fieldClass}
                >
                  <option value="">{t('pages.tenants.selectPlan')}</option>
                  {plans
                    .filter((p) => p.isActive())
                    .map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.nombre} - ${plan.precio.toLocaleString('es-AR')}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className={labelClass} htmlFor="reg-pay-method">
                  {t('pages.tenants.paymentMethod')}
                </label>
                <select
                  id="reg-pay-method"
                  value={formData.medioPago || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      medioPago: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                  className={fieldClass}
                  required
                >
                  <option value="">{t('pages.tenants.selectPaymentMethod')}</option>
                  {mockPaymentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.descripcion}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900/40">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t('pages.tenants.amount')}
                </span>
                <span className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                  {Number(formData.monto || 0).toLocaleString('es-AR', {
                    style: 'currency',
                    currency: 'ARS',
                  })}
                </span>
              </div>
              <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                {vencerDeuda
                  ? (() => {
                      const planId = formData.planId
                        ? parseInt(formData.planId, 10)
                        : tenant.planAdminId;
                      const plan = planId ? plans.find((p) => p.id === planId) : null;
                      const montoPlan = plan?.precio || 0;
                      return `Periodo: ${montoPlan.toLocaleString('es-AR', {
                        style: 'currency',
                        currency: 'ARS',
                      })} + Deuda: ${deudaAcumulada.toLocaleString('es-AR', {
                        style: 'currency',
                        currency: 'ARS',
                      })}`;
                    })()
                  : t('pages.tenants.amountNotEditable')}
              </p>
              {/* Valor en form nativo oculto para submit/validación HTML si hiciera falta */}
              <input type="hidden" name="monto" value={formData.monto} readOnly />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="reg-pay-notes">
                  {t('pages.tenants.notes')} ({t('common.optional')})
                </label>
                <textarea
                  id="reg-pay-notes"
                  value={formData.notas || ''}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className={`${fieldClass} min-h-[4.5rem] resize-y`}
                  rows={2}
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="reg-pay-file">
                  {t('pages.tenants.receipt')} ({t('common.optional')})
                </label>
                <input
                  id="reg-pay-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-teal-700 hover:file:bg-teal-100 dark:text-slate-400 dark:file:bg-teal-900/40 dark:file:text-teal-200"
                />
                {selectedFile && (
                  <p className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 dark:border-gray-600">
              <input
                type="checkbox"
                id="vencerDeuda"
                checked={vencerDeuda}
                onChange={(e) => handleVencerDeudaChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              <div className="min-w-0">
                <label
                  htmlFor="vencerDeuda"
                  className="cursor-pointer text-sm font-medium text-slate-800 dark:text-slate-100"
                >
                  {t('pages.tenants.settleDebt')}
                </label>
                {vencerDeuda && (
                  <p className="mt-0.5 text-[11px] text-teal-700 dark:text-teal-300">
                    {t('pages.tenants.settleDebtModeActive')}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </form>
    </ModalShell>
  );
};
